import { shiftToToday } from "./beanAging";
import { effectiveConfig, type PredictorConfig } from "./config";
import { compareDateTime, dateOf, daysBetween, type IsoDate } from "./dates";
import type { Bean, Grinder, Shot } from "./model";
import { classify } from "./predictor";

/**
 * Grinder slope learning. Mirrors SlopeLearning.java.
 *
 * Each bean with at least `slopeMinValidShots` valid shots spanning at least `slopeMinSpanSteps` dial steps gives its
 * own ordinary least-squares slope b_j of y = ln(t_norm) − aging shift (to the bean's last shot) against x, with
 * standard error se_j (residual SD floored at defaultResidualSd). Precision p_j = 1/se_j², recency
 * r_j = 0.5^(days since the bean's last shot / slopeHalfLifeD). The seed is worth `slopePseudoBeans` beans of the
 * recency-weighted mean precision p̄ = Σ r_j p_j / Σ r_j:
 *
 *   b = (Σ r_j p_j b_j + K p̄ b_seed) / (Σ r_j p_j + K p̄),   clamped to [2 b_seed, 0.5 b_seed]
 *
 * Nothing is stored: the learned slope is recomputed from the shots, so recalibrating (which hides older shots)
 * resets it.
 */
export interface BeanSlope {
  readonly beanId: number;
  readonly slope: number;
  readonly se: number;
  readonly validShots: number;
  readonly lastShotDate: IsoDate;
}

export interface LearnedSlope {
  readonly seed: number;
  readonly slope: number;
  readonly contributingBeans: number;
  /** "seed" when no bean qualified, else "learned" */
  readonly source: "seed" | "learned";
}

/** The bean's own slope on this grinder from its shots, or null when it does not qualify. */
export function beanSlope(g: Grinder, bean: Bean, shots: readonly Shot[], cfg: PredictorConfig): BeanSlope | null {
  const c = effectiveConfig(cfg, bean);
  let last: Shot | null = null;
  for (const s of shots) if (last === null || compareDateTime(s.timestamp, last.timestamp) > 0) last = s;
  if (last === null) return null;
  const lastDate = dateOf(last.timestamp);

  const xs: number[] = [];
  const ys: number[] = [];
  for (const s of shots) {
    const tNorm = (s.timeSeconds * c.targetYieldG) / s.yieldG;
    if (classify(tNorm, c) !== "valid") continue;
    xs.push(s.grindSetting);
    ys.push(Math.log(tNorm) - shiftToToday(bean, dateOf(s.timestamp), lastDate, c));
  }
  const n = xs.length;
  if (n < cfg.slopeMinValidShots) return null;
  let minX = Infinity, maxX = -Infinity;
  for (const x of xs) { if (x < minX) minX = x; if (x > maxX) maxX = x; }
  if (maxX - minX < cfg.slopeMinSpanSteps * g.dialResolution - 1e-9) return null;

  let mx = 0, my = 0;
  for (let i = 0; i < n; i++) { mx += xs[i] as number; my += ys[i] as number; }
  mx /= n; my /= n;
  let sxx = 0, sxy = 0;
  for (let i = 0; i < n; i++) {
    const dx = (xs[i] as number) - mx;
    sxx += dx * dx;
    sxy += dx * ((ys[i] as number) - my);
  }
  if (sxx <= 0) return null;
  const b = sxy / sxx;
  let ss = 0;
  for (let i = 0; i < n; i++) {
    const r = (ys[i] as number) - my - b * ((xs[i] as number) - mx);
    ss += r * r;
  }
  const sd = Math.max(Math.sqrt(ss / (n - 2)), cfg.defaultResidualSd);
  return { beanId: bean.id ?? -1, slope: b, se: sd / Math.sqrt(sxx), validShots: n, lastShotDate: lastDate };
}

/** Combine per-bean slopes (from beanSlope) into the grinder's learned slope as of `today`. */
export function combineSlopes(g: Grinder, beans: readonly BeanSlope[], today: IsoDate, cfg: PredictorConfig): LearnedSlope {
  const seed = g.priorSlopeB;
  if (beans.length === 0) return { seed, slope: seed, contributingBeans: 0, source: "seed" };
  let sumR = 0, sumRP = 0, sumRPB = 0;
  for (const b of beans) {
    const days = Math.max(0, daysBetween(b.lastShotDate, today));
    const r = Math.pow(0.5, days / cfg.slopeHalfLifeD);
    const p = 1 / (b.se * b.se);
    sumR += r;
    sumRP += r * p;
    sumRPB += r * p * b.slope;
  }
  const pBar = sumRP / sumR;
  const k = cfg.slopePseudoBeans * pBar;
  const raw = (sumRPB + k * seed) / (sumRP + k);
  const slope = Math.max(2 * seed, Math.min(0.5 * seed, raw));
  return { seed, slope, contributingBeans: beans.length, source: "learned" };
}

/** Everything in one call: `beans` are (bean, its active shots on g) pairs. */
export function learnSlope(g: Grinder, beans: readonly { bean: Bean; shots: readonly Shot[] }[], today: IsoDate,
                           cfg: PredictorConfig): LearnedSlope {
  const slopes: BeanSlope[] = [];
  for (const { bean, shots } of beans) {
    const s = beanSlope(g, bean, shots, cfg);
    if (s !== null) slopes.push(s);
  }
  return combineSlopes(g, slopes, today, cfg);
}

/** The grinder the predictor should see: its prior slope replaced by the learned one (same object when unchanged). */
export function withLearnedSlope(g: Grinder, learned: LearnedSlope): Grinder {
  return learned.slope === g.priorSlopeB ? g : { ...g, priorSlopeB: learned.slope };
}
